import React from 'react';
import { Award, Shield, CheckCircle, Phone } from 'lucide-react';

const MOTPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/21l2vqpb_Ministry%20of%20tourism.png"
          alt="Ministry of Tourism"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              Ministry of <span className="text-yellow-400">Tourism</span>
            </h1>
            <p className="text-xl text-gray-300">Promoting Safe Adventure Tourism in India</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Award className="text-yellow-400 flex-shrink-0 mt-1" size={40} />
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">Ministry of Tourism on Bungee Jumping</h2>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  The Ministry of Tourism (MOT) in India actively promotes adventure tourism as a niche segment and recognizes the growing popularity of bungee jumping in India as one of the most thrilling adventure sports in the country. The Ministry has introduced several initiatives to develop and promote safe and regulated bungee jumping experiences across India.
                </p>
                <p>
                  Popular destinations such as Goa, Rishikesh, Coorg, and Delhi have become major hubs for bungee jumping in India, offering professional setups that follow government safety regulations and international standards. These adventure zones provide visitors with safe yet adrenaline-pumping experiences that meet the expectations of both first-time jumpers and seasoned thrill-seekers.
                </p>
              </div>
            </section>

            {/* MOT Support */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Ministry Support for Adventure Tourism</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  In support of the industry, the Ministry of Tourism also provides various schemes and assistance to adventure tourism operators in India, helping them maintain high safety standards, professional training, and certified equipment.
                </p>
                <p>
                  The Adventure Tour Operators Association of India (ATOAI) plays a crucial role in maintaining guidelines and safety procedures for adventure activities, including bungee jumping, ensuring that operators have trained staff, proper safety gear, and effective risk management systems in place. While ATOAI does not directly regulate bungee jumping, it provides the overall framework that ensures safe adventure tourism in India.
                </p>
              </div>
            </section>

            {/* Safety Guidelines */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Safety Recommendations</h3>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                It is always recommended that individuals choose a reputable and certified bungee jumping operator who adheres to MOT and ATOAI guidelines. Factors like age, weight, and health restrictions should be verified before the jump to ensure complete safety and eligibility.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Choose MOT-recognized operators</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Verify safety certifications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Check age, weight, and health requirements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Follow all safety instructions</span>
                </li>
              </ul>
            </section>

            {/* Indya Bungy Recognition */}
            <section className="bg-gradient-to-br from-green-500/10 to-green-900/10 border border-green-500/20 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Indya Bungy Recognition</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Goa Bungy, operated by Indya Bungy, is one of the most trusted and registered bungee jumping operators in India, recognized by the Ministry of Tourism (MOT), Government of Goa, and Goa Tourism. We are also proud allied members of ATOAI (Adventure Tour Operators Association of India).
              </p>
              <p className="text-gray-300 leading-relaxed">
                Our facility offers a safe, certified, and thrilling bungee jumping experience in Goa, making it one of the most preferred spots for extreme adventure sports in India. At Indya Bungy, we are committed to offering India's best bungee jumping experiences, with the highest safety standards, professional crew, and globally approved equipment — ensuring that every jump is the perfect balance of safety and adrenaline.
              </p>
            </section>

            {/* CTA */}
            <section className="text-center">
              <a
                href="tel:8506865393"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Us: 8506865393</span>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MOTPage;
